import { Button, ButtonVariants } from "@app/shared";
import { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts";
import { Icons } from ".";

export type NoticeAction = {
    label: string;
    /** Route to navigate to when clicked. Ignored if `onClick` is provided. */
    to?: string;
    onClick?: () => void;
    primary?: boolean;
};

export type MissingResourceNoticeProps = {
    title: string;
    message: ReactNode;
    icon?: ReactNode;
    actions?: NoticeAction[];
    /** What to show in the top-nav center label (defaults to the title). */
    centerLabel?: ReactNode;
};

/**
 * Friendly full-page state shown when a location or device a user navigated to
 * (often from an alert email link) can't be displayed — deleted, unassigned, or
 * inaccessible. Replaces silent redirects so users get an explanation (#772).
 */
export const MissingResourceNotice: FC<MissingResourceNoticeProps> = ({ title, message, icon, actions, centerLabel }) => {
    const navigate = useNavigate();
    return (
        <DashboardLayout centerChildren={centerLabel ?? title}>
            <div className="flex flex-col items-center justify-center text-center max-w-xl mx-auto mt-16 gap-4">
                <div className="text-muted-body-text">{icon ?? <Icons.LocationOutline size={64} />}</div>
                <h2 className="text-2xl">{title}</h2>
                <p className="text-muted-body-text">{message}</p>
                {!!actions?.length && (
                    <div className="flex flex-wrap gap-3 justify-center mt-2">
                        {actions.map((a, i) => (
                            <Button
                                key={i}
                                variant={a.primary ? ButtonVariants.Primary : ButtonVariants.Secondary}
                                onClick={() => (a.onClick ? a.onClick() : a.to ? navigate(a.to) : undefined)}
                            >
                                {a.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};
